import type { Composition } from "../protocol/snapshot";

/**
 * Composition arithmetic. All quantities are integer units — these helpers
 * never introduce a fraction, so a long run can't drift.
 */

/**
 * The inert species residence sludge produces (03-hazards.md §2.5) and the
 * conventional Budget-tier impurity (GAPS.md §9.1 Q15). Named once here so
 * content, scoring and the inspector cannot disagree about its spelling.
 */
export const SLUDGE_SPECIES = "Sludge";

export function totalUnits(composition: Composition): number {
  let total = 0;
  for (const key of Object.keys(composition)) total += composition[key] ?? 0;
  return total;
}

/** Species keys in a stable (sorted) order, so iteration is deterministic. */
export function species(composition: Composition): string[] {
  return Object.keys(composition)
    .filter((key) => (composition[key] ?? 0) > 0)
    .sort();
}

export function addComposition(a: Composition, b: Composition): Composition {
  const out: Composition = { ...a };
  for (const key of species(b)) out[key] = (out[key] ?? 0) + (b[key] ?? 0);
  return out;
}

export function scaleComposition(composition: Composition, factor: number): Composition {
  const out: Composition = {};
  for (const key of species(composition)) out[key] = (composition[key] ?? 0) * factor;
  return out;
}

/** True when `available` holds at least every species/quantity in `required`. */
export function contains(available: Composition, required: Composition): boolean {
  for (const key of species(required)) {
    if ((available[key] ?? 0) < (required[key] ?? 0)) return false;
  }
  return true;
}

/**
 * Largest integer k such that `scaleComposition(required, k)` fits inside
 * `available`. Returns 0 when any required species is short, or when
 * `required` names no positive species. The Limiting Reagent Rule
 * (`01-simulation-model.md` §4): `k = min over species s in required of
 * floor(available[s] / required[s])`. Never builds a float ratio and never
 * returns `Infinity` — `species(required)` is non-empty by the time the loop
 * runs, so `k` is always reassigned away from its initial sentinel to a
 * finite `Math.floor` result before it is returned.
 */
export function maxMultiple(available: Composition, required: Composition): number {
  const keys = species(required);
  if (keys.length === 0) return 0;

  let k = Number.POSITIVE_INFINITY;
  for (const key of keys) {
    const ratio = Math.floor((available[key] ?? 0) / (required[key] ?? 0));
    if (ratio < k) k = ratio;
  }
  return k;
}

export function subtractComposition(a: Composition, b: Composition): Composition {
  const out: Composition = { ...a };
  for (const key of species(b)) {
    const remaining = (out[key] ?? 0) - (b[key] ?? 0);
    if (remaining > 0) out[key] = remaining;
    else delete out[key];
  }
  return out;
}

/**
 * Purity of the dominant species, 0-100 (protocol/snapshot.ts). A single-species
 * packet is 100; an even two-species mix is 50. Used for catalyst poisoning.
 */
export function purityOf(composition: Composition): number {
  const total = totalUnits(composition);
  if (total <= 0) return 0;
  let dominant = 0;
  for (const key of species(composition)) {
    const amount = composition[key] ?? 0;
    if (amount > dominant) dominant = amount;
  }
  return Math.round((dominant * 100) / total);
}

/**
 * True when `composition`'s share of `target`'s named species falls below
 * `minPurityPercent` — the catalyst poisoning gate (`03-hazards.md` §2.4;
 * `GAPS.md` §9.1 Q9 (e), resolved: "target" is the stage objective's species,
 * not the packet's own dominant one). Compared as `targetUnits * 100 <
 * minPurityPercent * total`, cross-multiplied so the threshold decision never
 * divides or rounds — unlike `purityOf`, a display quantity, this is what
 * makes 94.5 % poison instead of rounding up to a pass. `targetUnits` sums the
 * packet's composition over every species `target` names with a positive
 * amount, mirroring `tick.ts`'s `collectAtHoppers`. An empty packet
 * (`total === 0`) has nothing to be off-spec about — nothing arrived, so it
 * does not poison the bed.
 */
export function isBelowTargetPurity(
  composition: Composition,
  target: Composition,
  minPurityPercent: number,
): boolean {
  const total = totalUnits(composition);
  if (total <= 0) return false;

  let targetUnits = 0;
  for (const key of species(composition)) {
    if ((target[key] ?? 0) > 0) targetUnits += composition[key] ?? 0;
  }

  return targetUnits * 100 < minPurityPercent * total;
}

/**
 * Removes `units` integer units total from a composition — a leaking pipe's
 * flat per-tick loss (`docs/design/03-hazards.md` §2.3; spec 0004). Each unit
 * comes off the species holding the largest remaining quantity, ties broken by
 * the first key in `species()`'s sorted order, so the mixture drains toward
 * its dominant species and the result is deterministic regardless of
 * insertion order. Draining more units than the composition holds simply
 * empties it — there's nothing left to remove once every species reaches 0.
 */
export function drainUnits(composition: Composition, units: number): Composition {
  const out: Composition = { ...composition };
  for (let i = 0; i < units; i++) {
    let target: string | null = null;
    let max = 0;
    for (const key of species(out)) {
      const amount = out[key] ?? 0;
      if (amount > max) {
        max = amount;
        target = key;
      }
    }
    if (target === null) break;
    const remaining = (out[target] ?? 0) - 1;
    if (remaining > 0) out[target] = remaining;
    else delete out[target];
  }
  return out;
}

/**
 * Converts 1 unit of a composition into `SLUDGE_SPECIES` (`03-hazards.md`
 * §2.5; spec 0014) — residence sludge's product. The unit comes off the
 * species holding the largest quantity, ties broken by the first key in
 * `species()`'s sorted order, mirroring `drainUnits`' rule so the two ways
 * the engine takes a unit out of a packet agree on which one. Mass is
 * conserved: the unit moves into `Sludge` rather than disappearing. If the
 * largest species is already `Sludge`, this is a documented no-op on the
 * composition — decrementing and re-incrementing the same key — not a case
 * that needs to be special-cased out.
 */
export function convertUnitToSludge(composition: Composition): Composition {
  const out: Composition = { ...composition };

  let target: string | null = null;
  let max = 0;
  for (const key of species(out)) {
    const amount = out[key] ?? 0;
    if (amount > max) {
      max = amount;
      target = key;
    }
  }
  if (target === null) return out; // nothing to convert

  const remaining = (out[target] ?? 0) - 1;
  if (remaining > 0) out[target] = remaining;
  else delete out[target];

  out[SLUDGE_SPECIES] = (out[SLUDGE_SPECIES] ?? 0) + 1;
  return out;
}

/** Deep equality for the golden-run assertions. */
export function compositionsEqual(a: Composition, b: Composition): boolean {
  const keysA = species(a);
  const keysB = species(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((key, i) => key === keysB[i] && a[key] === b[key]);
}
